// (c) 2023 ABBYY Development, Inc.

import { useCallback, Dispatch, SetStateAction, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
    Gaped,
    Pagination,
    Box,
    MuiTable,
    TableBody,
    TableCell as BaseCell,
    TableContainer,
    TableHead,
    TableRow
} from 'components';
import { ExtendedTable } from 'hooks';
import Sort from './Sort';
import Filter from './Filter';
import { TableCellProps } from '@mui/material';

interface TableCellExtendedProps extends TableCellProps {
    width?: number | string;
    maxWidth?: number | string;
}

const getWidth = (width: number | string) => {
    if (typeof width === 'string') {
        return width;
    } else if (typeof width === 'number') {
        return `${width}px`;
    } else return 'unset';
};

const TableCell = styled(BaseCell)<TableCellExtendedProps>`
    max-width: ${(props) =>
        props.maxWidth ? getWidth(props.maxWidth) : getWidth(props.width)};
    text-overflow: ellipsis;
    overflow: hidden;
`;

interface TableProps<T extends object> {
    resourceId?: string;
    currentPage?: number;
    pageCount?: number;
    setCurrentPage?: Dispatch<SetStateAction<number>>;
    controller: ExtendedTable<T>;
    disablePagination?: boolean;
}

export function Table<T extends object>({
    resourceId,
    setCurrentPage,
    currentPage,
    pageCount,
    controller,
    disablePagination
}: TableProps<T>): ReactElement {
    const { t } = useTranslation();

    const handleChangePage = useCallback(
        (_: any, page: number) => {
            if (!disablePagination) {
                setCurrentPage(page);
            }
        },
        [setCurrentPage, disablePagination]
    );

    const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } =
        controller;

    return (
        <>
            <TableContainer style={{ flexGrow: 1 }}>
                <MuiTable stickyHeader {...getTableProps()}>
                    <TableHead>
                        {headerGroups.map((headerGroup) => (
                            <TableRow
                                style={{ whiteSpace: 'nowrap' }}
                                {...headerGroup.getHeaderGroupProps()}
                            >
                                {headerGroup.headers.map((column, index) => (
                                    <TableCell
                                        width={index === 0 ? 40 : null}
                                        {...column.getHeaderProps()}
                                    >
                                        <Gaped spacing={1}>
                                            {column.render('Header')}
                                            {controller.hasSort &&
                                                !column.disableSortBy && (
                                                    <Sort
                                                        resourceId={resourceId}
                                                        columnId={column.id}
                                                    />
                                                )}
                                            {column.filter && (
                                                <Filter
                                                    resourceId={resourceId}
                                                    columnId={column.id}
                                                    filterType={
                                                        column.filter as string
                                                    }
                                                    // @ts-ignore
                                                    options={column.options}
                                                    defaultMax={
                                                        // @ts-ignore
                                                        column.defaultMax
                                                    }
                                                    defaultMin={
                                                        // @ts-ignore
                                                        column.defaultMin
                                                    }
                                                />
                                            )}
                                        </Gaped>
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody {...getTableBodyProps()}>
                        {rows.map((row, i) => {
                            prepareRow(row);
                            return (
                                <TableRow {...row.getRowProps()}>
                                    {row.cells.map((cell) => (
                                        <TableCell
                                            // @ts-ignore
                                            align={cell.column.align || 'left'}
                                            {...cell.getCellProps()}
                                        >
                                            {cell.render('Cell')}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </MuiTable>
            </TableContainer>
            {!disablePagination && (
                <Box p={2} display="flex" justifyContent="flex-end">
                    <Pagination
                        nextButtonText={t('general.Next')}
                        previousButtonText={t('general.Prev')}
                        onChange={handleChangePage}
                        count={pageCount}
                        page={currentPage}
                    />
                </Box>
            )}
        </>
    );
}
